import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { listingsAPI, offersAPI, sellersAPI, storesAPI, deliveryFeeAPI, Listing, Offer, Store } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import OfferModal from '../components/OfferModal';
import { createSlug } from '../utils/slug';
import { getViewerDeviceId } from '../viewerDeviceId';
import MapboxMapWrapper from '../components/MapboxMapWrapper';
import ProductImageZoom from '../components/ProductImageZoom';
import ListingImage from '../components/ListingImage';
import {
  cardDisplayPrice,
  discountBadgeText,
  formatListingPrice,
  strikethroughListPrice,
} from '../utils/listingPricing';
import {
  deliveryFeeLabel,
  deliveryFeeTooltipLines,
  listingDeliveryTitle,
  hoursScheduleTitle,
  listingDeliveryTitleWithTime,
  hoursScheduleSubtitle,
  hoursUnavailableDetailLabel,
  deliveryTimeLabel,
  formatStoreTime,
  getStoreOpenStatus,
  hoursStatusBadgeLabel,
  getTodayStoreHours,
  orderWithinLabel,
  parseStoreHours,
  sellerAreaLabel,
  storeClosedLabel,
  sellerHoursSourceFromListing,
} from '../utils/listingDeliveryInfo';
import {
  deliveryAreaLabel,
  hasUserDeliveryLocation,
  loadHomeDeliveryLocation,
} from '../utils/homeLocationStorage';
import { peekHomeCachedListing } from '../utils/homeScrollRestoration';
import {
  getCachedListingCardImage,
  ListingDetailNavState,
  takePrefetchedListingDetail,
} from '../utils/listingImageCache';
import { listingImageUrl } from '../utils/listingImageUrl';

const loginStateFromListing = (listingId: string) =>
  ({ state: { from: `/listings/${listingId}` } } as const);

function listingSupportsItemReviews(quantity?: number | null): boolean {
  return (Number(quantity) || 1) > 1;
}

function listingShowsItemReviews(listing: {
  quantity?: number | null;
  item_rating?: { total_ratings?: number } | null;
} | null | undefined): boolean {
  if (!listing || !listingSupportsItemReviews(listing.quantity)) return false;
  return (listing.item_rating?.total_ratings ?? 0) > 0;
}

function listingImages(listing: {
  image_url?: string | null;
  image_urls?: string[] | null;
} | null | undefined): string[] {
  if (!listing) return [];
  if (listing.image_urls && listing.image_urls.length > 0) return listing.image_urls;
  if (listing.image_url) return [listing.image_url];
  return [];
}

const DESCRIPTION_PREVIEW_CHARS = 220;

function listingDescriptionPreview(text: string): { preview: string; isLong: boolean } {
  const trimmed = text.trim();
  if (trimmed.length <= DESCRIPTION_PREVIEW_CHARS) {
    return { preview: trimmed, isLong: false };
  }
  const cut = trimmed.slice(0, DESCRIPTION_PREVIEW_CHARS).replace(/\s+\S*$/, '');
  return {
    preview: cut || trimmed.slice(0, DESCRIPTION_PREVIEW_CHARS),
    isLong: true,
  };
}

function listingMapCoords(lat: unknown, lon: unknown): [number, number] | null {
  const latitude = Number(lat);
  const longitude = Number(lon);
  if (
    !Number.isFinite(latitude) ||
    !Number.isFinite(longitude) ||
    latitude < -90 ||
    latitude > 90 ||
    longitude < -180 ||
    longitude > 180
  ) {
    return null;
  }
  return [latitude, longitude];
}

function seedListingFromNav(
  id: string | undefined,
  nav: ListingDetailNavState | null,
): Listing | null {
  const listingId = id ? parseInt(id, 10) : NaN;
  if (!Number.isFinite(listingId)) return null;
  if (nav?.listing && Number(nav.listing.id) === listingId) {
    return nav.listing;
  }
  return peekHomeCachedListing(listingId);
}

const ListingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { addToCart } = useCart();
  const navState = (location.state || null) as ListingDetailNavState | null;
  const initialSeed = useMemo(() => seedListingFromNav(id, navState), [id, navState]);
  const previewFromNav = (navState?.previewImageUrl || '').trim();
  const [listing, setListing] = useState<Listing | null>(initialSeed);
  const [loading, setLoading] = useState(!initialSeed);
  const [error, setError] = useState('');
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [sellerReviews, setSellerReviews] = useState<any[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [itemReviews, setItemReviews] = useState<any[]>([]);
  const [loadingItemReviews, setLoadingItemReviews] = useState(false);
  const [store, setStore] = useState<Store | null>(null);
  const [loadingStore, setLoadingStore] = useState(false);
  const [mapCoords, setMapCoords] = useState<[number, number] | null>(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const [showAllItemReviewsModal, setShowAllItemReviewsModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [sellerProfile, setSellerProfile] = useState<any>(null);
  const [showMoreReviews, setShowMoreReviews] = useState(false);
  const [processingBuyNow, setProcessingBuyNow] = useState(false);
  const [openingOfferModal, setOpeningOfferModal] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [recommended, setRecommended] = useState<any[]>([]);
  const [recommendationArea, setRecommendationArea] = useState<string | null>(null);
  const [showWeeklyHours, setShowWeeklyHours] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeBusy, setLikeBusy] = useState(false);
  const [buyerDistanceMiles, setBuyerDistanceMiles] = useState<number | null>(null);
  const [showFullDescription, setShowFullDescription] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    const seed = seedListingFromNav(id, (location.state || null) as ListingDetailNavState | null);
    setListing(seed);
    setLoading(!seed);
    setError('');
    setSelectedImageIndex(0);
    loadListing();
    setSelectedQuantity(1); // Reset quantity when listing changes
    setShowFullDescription(false);
    setProcessingBuyNow(false);
    setIsLiked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!user || !id) {
      setIsLiked(false);
      return;
    }
    listingsAPI
      .getLikeStatus(parseInt(id, 10))
      .then((data) => setIsLiked(!!data.liked))
      .catch(() => setIsLiked(false));
  }, [user, id]);

  useEffect(() => {
    if (listing && user && id) {
      loadOffers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing, user, id]);

  useEffect(() => {
    if (listing && listing.user_id) {
      loadSellerReviews();
      loadSellerProfile();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing]);

  useEffect(() => {
    if (listing && listing.id) {
      loadItemReviews();
    }
    if (listing && listing.store_id) {
      loadStore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing]);

  useEffect(() => {
    if (listing && listing.store_id) {
      loadStore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listing]);

  const loadRecommended = useCallback(async () => {
    const stored = hasUserDeliveryLocation() ? loadHomeDeliveryLocation() : null;
    setRecommendationArea(stored ? deliveryAreaLabel(stored) : null);
    try {
      const { items } = await listingsAPI.getRecommended(20, {
        city: stored?.city,
        state: stored?.state,
      });
      const exclude = id ? parseInt(id, 10) : 0;
      setRecommended((items || []).filter((i: any) => i.id !== exclude && Number(i.sold || 0) <= 0));
    } catch {
      setRecommended([]);
    }
  }, [id]);

  useEffect(() => {
    loadRecommended();
  }, [loadRecommended]);

  useEffect(() => {
    const onLocationUpdate = () => {
      loadRecommended();
    };
    window.addEventListener('delivery-location-updated', onLocationUpdate);
    return () => window.removeEventListener('delivery-location-updated', onLocationUpdate);
  }, [loadRecommended]);

  useEffect(() => {
    if (!listing?.id) {
      setBuyerDistanceMiles(null);
      return;
    }
    let cancelled = false;

    const resolveBuyerDistance = async () => {
      const stored = hasUserDeliveryLocation() ? loadHomeDeliveryLocation() : null;
      if (!stored) {
        if (!cancelled) setBuyerDistanceMiles(null);
        return;
      }
      try {
        // Prefer address string (same as checkout) so stale cached GPS cannot skew the number.
        const address =
          (stored.label || '').trim() ||
          [stored.city, stored.state].filter(Boolean).join(', ') ||
          (stored.zip || '').trim();
        if (address && /[a-zA-Z]/.test(address)) {
          const fee = await deliveryFeeAPI.calculate(listing.id, address, 0);
          if (!cancelled && fee?.distance != null && Number.isFinite(Number(fee.distance))) {
            setBuyerDistanceMiles(Number(fee.distance));
            return;
          }
        }
        if (stored.lat != null && stored.lng != null) {
          const loc = await deliveryFeeAPI.getListingLocation(listing.id, stored.lat, stored.lng);
          if (!cancelled && loc?.distance_miles != null && Number.isFinite(Number(loc.distance_miles))) {
            setBuyerDistanceMiles(Number(loc.distance_miles));
            return;
          }
        }
        if (!cancelled) setBuyerDistanceMiles(null);
      } catch {
        if (!cancelled) setBuyerDistanceMiles(null);
      }
    };

    resolveBuyerDistance();
    const onLocationUpdate = () => {
      resolveBuyerDistance();
    };
    window.addEventListener('delivery-location-updated', onLocationUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener('delivery-location-updated', onLocationUpdate);
    };
  }, [listing?.id]);

  useEffect(() => {
    if (!listing) return;
    let cancelled = false;

    const resolveMapCoords = async () => {
      const saved = listingMapCoords(listing.latitude, listing.longitude);
      if (saved) {
        setMapCoords(saved);
        setMapLoading(false);
        return;
      }

      setMapLoading(true);
      setMapCoords(null);
      try {
        const loc = await deliveryFeeAPI.getListingLocation(listing.id);
        const coords = listingMapCoords(loc?.latitude, loc?.longitude);
        if (!cancelled) setMapCoords(coords);
      } catch (err) {
        console.error('Error loading map location:', err);
        if (!cancelled) setMapCoords(null);
      } finally {
        if (!cancelled) setMapLoading(false);
      }
    };

    resolveMapCoords();
    return () => {
      cancelled = true;
    };
  }, [listing?.id, listing?.latitude, listing?.longitude]);

  const loadOffers = async () => {
    if (!listing || !id) return;
    setLoadingOffers(true);
    try {
      const data = await offersAPI.getByListing(parseInt(id));
      setOffers(data);
    } catch (err: any) {
      console.error('Error loading offers:', err);
    } finally {
      setLoadingOffers(false);
    }
  };

  const loadListing = async () => {
    if (!id) return;
    const listingId = parseInt(id, 10);
    try {
      const prefetched = takePrefetchedListingDetail(listingId);
      const data = prefetched
        ? await prefetched
        : await listingsAPI.getOne(listingId);
      setListing(data);
      setSelectedQuantity(1); // Reset quantity when listing loads
      listingsAPI.recordView(listingId, getViewerDeviceId()).catch(() => {});
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load listing');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleLike = async () => {
    if (!id) return;
    if (!user) {
      navigate('/login', loginStateFromListing(id));
      return;
    }
    if (likeBusy) return;
    setLikeBusy(true);
    try {
      const { liked } = await listingsAPI.toggleLike(parseInt(id, 10));
      setIsLiked(!!liked);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Could not update saved listing');
    } finally {
      setLikeBusy(false);
    }
  };

  const likeButton = (
    <button
      type="button"
      onClick={handleToggleLike}
      disabled={likeBusy}
      className="absolute top-3 right-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-sm border border-gray-200 hover:bg-white disabled:opacity-60"
      aria-label={isLiked ? 'Remove from saved' : 'Save listing'}
      aria-pressed={isLiked}
    >
      <i
        className={`text-xl leading-none ${isLiked ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-gray-700'}`}
        aria-hidden
      />
    </button>
  );

  const loadSellerReviews = async () => {
    if (!listing || !listing.user_id) return;
    // Store listings load reviews from the store (see loadStore).
    if (listing.store_id) return;
    setLoadingReviews(true);
    try {
      const profile = await sellersAPI.getProfile(listing.user_id);
      if (profile && profile.ratings && profile.ratings.reviews) {
        setSellerReviews(profile.ratings.reviews);
      } else {
        setSellerReviews([]);
      }
    } catch (err: any) {
      console.error('Error loading seller reviews:', err);
      setSellerReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  };

  const loadSellerProfile = async () => {
    if (!listing || !listing.user_id) return;
    try {
      const profile = await sellersAPI.getProfile(listing.user_id);
      setSellerProfile(profile);
    } catch (err: any) {
      console.error('Error loading seller profile:', err);
    }
  };

  const loadItemReviews = async () => {
    if (!listing || !listing.id) return;
    if (!listingSupportsItemReviews(listing.quantity)) {
      setItemReviews([]);
      return;
    }
    setLoadingItemReviews(true);
    try {
      const reviews = await listingsAPI.getItemReviews(listing.id);
      setItemReviews(reviews || []);
    } catch (err: any) {
      console.error('Error loading item reviews:', err);
      setItemReviews([]);
    } finally {
      setLoadingItemReviews(false);
    }
  };

  const loadStore = async () => {
    if (!listing || !listing.store_id) return;
    setLoadingStore(true);
    setLoadingReviews(true);
    try {
      const storeData = await storesAPI.getOne(listing.store_id);
      setStore(storeData);
      setSellerReviews(storeData?.ratings?.reviews || []);
    } catch (err: any) {
      console.error('Error loading store:', err);
      setSellerReviews([]);
    } finally {
      setLoadingStore(false);
      setLoadingReviews(false);
    }
  };

  const renderStars = (rating: number) => {
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`text-lg ${
              star <= rating ? 'text-yellow-400' : 'text-gray-300'
            }`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  if (loading && !listing) {
    if (previewFromNav) {
      return (
        <div className="min-h-screen bg-gray-50 py-4 sm:py-6 lg:py-8">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
            <button
              onClick={() => navigate(-1)}
              className="mb-4 sm:mb-6 flex items-center space-x-2 text-gray-600 hover:text-gray-900 text-sm font-medium"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Back to listings</span>
            </button>
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="bg-gray-100 flex items-center justify-center p-2 sm:p-4 min-h-[300px]">
                <img
                  src={previewFromNav}
                  alt=""
                  className="w-full max-w-full max-h-[600px] h-auto object-contain rounded-xl"
                />
              </div>
              <div className="p-4 sm:p-6 space-y-3 animate-pulse">
                <div className="h-6 bg-gray-200 rounded w-2/3" />
                <div className="h-4 bg-gray-100 rounded w-1/3" />
                <div className="h-4 bg-gray-100 rounded w-1/2" />
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
          <p className="mt-4 text-gray-600">Loading listing...</p>
        </div>
      </div>
    );
  }

  if ((error && !listing) || !listing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600">{error || 'Listing not found'}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const isOwner = user && user.id === listing.user_id;
  const isSold = listing.sold === 1 || listing.sold === true;
  const discountText = discountBadgeText(listing);
  const displayPrice = cardDisplayPrice(listing);
  const listPriceStrike = strikethroughListPrice(listing);
  const isStoreListing = !!listing.store_id;
  const sellerHoursSource = sellerHoursSourceFromListing(listing);
  const hoursSource = isStoreListing ? store : sellerHoursSource;
  const hoursObj = parseStoreHours(hoursSource);
  const todayHours = getTodayStoreHours(hoursSource);
  const storeOpenStatus = getStoreOpenStatus(hoursSource);
  const orderWithin = hoursSource ? orderWithinLabel(hoursSource) : null;
  const closedLabel = isStoreListing
    ? storeClosedLabel(store)
    : hoursUnavailableDetailLabel(sellerHoursSource, 'seller');
  const isScheduledForLater = storeOpenStatus.isOpen === false && !!hoursSource;
  const deliveryTime = deliveryTimeLabel(listing);
  const scheduleTitle = hoursScheduleTitle(
    isStoreListing ? 'store' : 'seller',
    deliveryTime
  );
  const scheduleSubtitle = isScheduledForLater
    ? hoursScheduleSubtitle(hoursSource)
    : null;
  const deliveryTitle = listingDeliveryTitleWithTime(
    listingDeliveryTitle(listing, {
      isStoreListing,
      hoursSource,
      isOpen: storeOpenStatus.isOpen,
    }),
    deliveryTime
  );
  const hoursBadgeLabel = hoursStatusBadgeLabel(hoursSource, isStoreListing ? 'store' : 'seller');
  const hoursSectionTitle = isStoreListing ? 'Store hours' : 'Seller hours';
  const descriptionText = (listing.description || '').trim();
  const descriptionPreview = listingDescriptionPreview(descriptionText);
  const deliveryFee = !orderWithin && !deliveryTime ? deliveryFeeLabel(listing) : null;
  const rawViews = (listing as Listing & { views_count?: number }).views_count;
  const viewsCount = typeof rawViews === 'number' ? rawViews : null;
  const sellerName =
    listing.store_name || listing.shop_store_name || listing.seller_name || 'Seller';
  const sellerAvatar =
    store?.profile_picture ||
    listing.store_profile_picture ||
    sellerProfile?.seller?.profile_picture ||
    listing.seller_profile_picture;

  // Prefer counts > 0 within the correct rating system only.
  // Store listings → store ratings; personal listings → seller ratings. Never mix.
  const pickPositiveTotal = (...values: Array<number | null | undefined>) => {
    for (const v of values) {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) return n;
    }
    return 0;
  };
  const badgeTotal = listing.store_id
    ? pickPositiveTotal(
        listing.store_badge?.total_ratings,
        store?.ratings?.total,
        sellerReviews.length
      )
    : pickPositiveTotal(
        listing.seller_badge?.total_ratings,
        sellerProfile?.ratings?.total,
        sellerReviews.length
      );
  const hasFeedback = badgeTotal > 0;

  const positiveLabel = ((): string | null => {
    const formatPct = (pct: number) => {
      const safe = Number(pct);
      if (!Number.isFinite(safe)) return null;
      const formatted = safe % 1 === 0 ? safe.toString() : safe.toFixed(1);
      return `${formatted}% Buyer Approved`;
    };
    const candidates: Array<{ total?: number | null; pct?: number | null }> = listing.store_id
      ? [
          { total: listing.store_badge?.total_ratings, pct: listing.store_badge?.positive_percentage },
          { total: store?.ratings?.total, pct: store?.ratings?.positive_percentage },
        ]
      : [
          { total: listing.seller_badge?.total_ratings, pct: listing.seller_badge?.positive_percentage },
          { total: sellerProfile?.ratings?.total, pct: sellerProfile?.ratings?.positive_percentage },
        ];
    for (const c of candidates) {
      if ((c.total ?? 0) > 0 && c.pct != null) {
        const label = formatPct(Number(c.pct));
        if (label) return label;
      }
    }
    return null;
  })();
  const conditionLabel =
    listing.condition && listing.category !== 'food' && listing.category !== 'shop'
      ? listing.condition === 'new'
        ? 'New'
        : listing.condition === 'new_open_box'
          ? 'New – Open box'
          : listing.condition === 'seller_refurbished'
            ? 'Seller refurbished'
            : listing.condition === 'used'
              ? 'Used'
              : listing.condition === 'for_parts'
                ? 'For parts or not working'
                : listing.condition
      : null;
  const sellerArea = sellerAreaLabel(listing);
  const locationAddress =
    listing.shop_store_address ||
    listing.address ||
    store?.address ||
    (listing.city_name && listing.state_abbreviation
      ? `${listing.city_name}, ${listing.state_abbreviation}`
      : null);
  const storeHoursObj = hoursObj;
  const todayStoreHours = todayHours;
  const maxDeliveryMiles = (() => {
    const fromListing = listing.max_delivery_miles;
    if (fromListing != null && Number(fromListing) > 0) return Number(fromListing);
    const fromStore = (store as Store & { max_delivery_miles?: number | null })?.max_delivery_miles;
    if (fromStore != null && Number(fromStore) > 0) return Number(fromStore);
    return null;
  })();
  const deliveryFeeTooltip = deliveryFeeTooltipLines(listing);

  const openFeedbackModal = async () => {
    setShowFeedbackModal(true);
    setLoadingFeedback(true);
    try {
      if (listing.store_id) {
        const feedback = await storesAPI.getFeedback(listing.store_id);
        setFeedbackData(feedback);
      } else {
        const feedback = await sellersAPI.getFeedback(listing.user_id);
        setFeedbackData(feedback);
      }
    } catch (err) {
      console.error('Error loading feedback:', err);
    } finally {
      setLoadingFeedback(false);
    }
  };

  const soldByCard = (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
      <h3 className="font-semibold text-gray-900 mb-4">Sold by</h3>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-12 h-12 flex-shrink-0">
          {sellerAvatar ? (
            <img
              src={sellerAvatar}
              alt={sellerName}
              className="w-12 h-12 rounded-full object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white text-lg font-bold">
              {sellerName.charAt(0).toUpperCase() || '?'}
            </div>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-bold text-gray-900">{sellerName}</p>
            {listing.store_id && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-xs font-medium text-gray-700">
                <i className="ri-store-3-line" aria-hidden />
                Store
              </span>
            )}
          </div>
          {!hasFeedback ? (
            <p className="text-sm text-gray-600 mt-1">New seller</p>
          ) : null}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-gray-700 mb-3">
        <i className="ri-thumb-up-line text-gray-500 shrink-0" aria-hidden />
        {hasFeedback ? (
          <>
            {positiveLabel ? (
              <button
                type="button"
                onClick={openFeedbackModal}
                className="hover:text-green-600 transition-colors underline text-left"
              >
                {positiveLabel}
              </button>
            ) : null}
            {positiveLabel && (
              <span className="text-gray-300" aria-hidden>
                ·
              </span>
            )}
            <span>
              {badgeTotal} {badgeTotal === 1 ? 'rating' : 'ratings'}
            </span>
          </>
        ) : (
          <span className="text-gray-500">No feedback yet</span>
        )}
      </div>
      <p className="text-sm text-gray-600 mb-4 flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
        <i className="ri-store-2-line shrink-0 text-gray-500" aria-hidden />
        <span>{listing.store_id ? 'Local store' : 'Local seller'}</span>
        {sellerArea && (
          <>
            <span className="text-gray-300" aria-hidden>
              ·
            </span>
            <span>{sellerArea}</span>
          </>
        )}
      </p>
      {listing.store_id ? (
        <Link
          to={`/stores/${createSlug(listing.store_name || listing.shop_store_name || 'store')}`}
          className="inline-flex items-center gap-1 text-sm font-semibold text-green-600 hover:text-green-700"
        >
          View store
          <i className="ri-arrow-right-line" aria-hidden />
        </Link>
      ) : (
        <Link
          to={`/sellers/${listing.user_id}`}
          className="inline-flex items-center gap-1 text-sm font-semibold text-green-600 hover:text-green-700"
        >
          Seller&apos;s other items
          <i className="ri-arrow-right-line" aria-hidden />
        </Link>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-4 sm:py-6 lg:py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <button
          onClick={() => navigate(-1)}
          className="mb-4 sm:mb-6 flex items-center space-x-2 text-gray-600 hover:text-gray-900 text-sm font-medium transition-all hover:translate-x-[-4px]"
        >
          <i className="ri-arrow-left-line"></i>
          <span>Back to listings</span>
        </button>

        <div className="grid gap-4 sm:gap-6 lg:grid-cols-[1fr_380px] xl:grid-cols-[1fr_420px] lg:items-start">
          <div className="lg:col-start-1 lg:row-start-1 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-100 relative overflow-hidden rounded-xl" style={{ minHeight: '400px' }}>
              {likeButton}
              {(() => {
                const images = listingImages(listing);
                const previewFor = (original: string, index: number) => {
                  if (index === 0 && navState?.previewImageUrl) {
                    return navState.previewImageUrl;
                  }
                  return (
                    getCachedListingCardImage(original) ||
                    listingImageUrl(original, 'card') ||
                    original
                  );
                };
                
                if (images.length === 0) {
                  return (
                    <div className="w-full aspect-square flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl">
                      <i className="ri-image-line text-gray-300 text-8xl"></i>
                    </div>
                  );
                }

                // Show all images with vertical thumbnails if there are multiple images
                if (images.length > 1) {
                  return (
                    <div className="flex flex-col md:flex-row h-full">
                      {/* Vertical Thumbnails - Desktop Only (md and above) - On Left Side */}
                      <div className="hidden md:flex flex-col gap-2 p-2 overflow-y-auto border-r border-gray-200" style={{ maxHeight: '100%', width: '100px' }}>
                        {images.map((img, idx) => (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedImageIndex(idx);
                            }}
                            className={`relative w-full aspect-square flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                              selectedImageIndex === idx 
                                ? 'border-green-500 ring-2 ring-green-300 shadow-md' 
                                : 'border-gray-300 hover:border-green-400 opacity-75 hover:opacity-100 hover:shadow-sm'
                            }`}
                          >
                            <ListingImage
                              src={img}
                              size="thumb"
                              alt={`Thumbnail ${idx + 1}`}
                              className="w-full h-full object-cover"
                              loading={idx === selectedImageIndex ? 'eager' : 'lazy'}
                              decoding="async"
                            />
                            {selectedImageIndex === idx && (
                              <div className="absolute inset-0 bg-green-500 bg-opacity-20 flex items-center justify-center">
                                <i className="ri-check-line text-white text-sm"></i>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                      
                      {/* Main Image Display - rounded corners, no edge overlay */}
                      <div className="flex-1 flex w-full max-[611px]:items-center md:items-start justify-center bg-gray-50 max-[611px]:min-h-[300px] p-2 sm:p-4">
                        <ProductImageZoom
                          src={images[selectedImageIndex]}
                          placeholderSrc={previewFor(images[selectedImageIndex], selectedImageIndex)}
                          alt={`${listing.title} - Image ${selectedImageIndex + 1}`}
                        />
                      </div>
                      
                      {/* Thumbnails Below Main Image - Between 612px and md (when layout collapses) */}
                      <div className="hidden min-[612px]:flex md:hidden gap-2 p-2 overflow-x-auto border-t border-gray-200 bg-white">
                        {images.map((img, idx) => (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedImageIndex(idx);
                            }}
                            className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                              selectedImageIndex === idx 
                                ? 'border-green-500 ring-2 ring-green-300 shadow-md' 
                                : 'border-gray-300 hover:border-green-400 opacity-75 hover:opacity-100'
                            }`}
                          >
                            <ListingImage
                              src={img}
                              size="thumb"
                              alt={`Thumbnail ${idx + 1}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                            {selectedImageIndex === idx && (
                              <div className="absolute inset-0 bg-green-500 bg-opacity-20 flex items-center justify-center">
                                <i className="ri-check-line text-white text-xs"></i>
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                      
                      {/* Mobile Thumbnails - Below Main Image (611px and below) */}
                      <div className="hidden max-[611px]:flex gap-2 p-2 overflow-x-auto border-t border-gray-200 bg-white">
                        {images.map((img, idx) => (
                          <button
                            key={idx}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedImageIndex(idx);
                            }}
                            className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                              selectedImageIndex === idx 
                                ? 'border-green-500 ring-2 ring-green-300' 
                                : 'border-gray-300 hover:border-green-400'
                            }`}
                          >
                            <ListingImage
                              src={img}
                              size="thumb"
                              alt={`Thumbnail ${idx + 1}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                }

                // Single image display
                return (
                  <div className="flex w-full items-center justify-center p-2 sm:p-4 bg-gray-50 min-h-[300px]">
                    <ProductImageZoom
                      src={images[0]}
                      placeholderSrc={previewFor(images[0], 0)}
                      alt={listing.title}
                    />
                  </div>
                );
              })()}
            </div>
          </div>

          <div className="lg:col-start-2 lg:row-start-1 lg:row-span-2 self-start min-w-0 space-y-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
              {isSold && (
                <span className="inline-block bg-red-500 text-white px-3 py-1.5 rounded-full font-bold text-sm mb-4">
                  SOLD
                </span>
              )}
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 leading-tight">
                {listing.title}
              </h1>
              <div className="mt-2">
                {listing.store_id ? (
                  <Link
                    to={`/stores/${createSlug(listing.store_name || listing.shop_store_name || 'store')}`}
                    className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {sellerName}
                  </Link>
                ) : (
                  <Link
                    to={`/sellers/${listing.user_id}`}
                    className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {sellerName}
                  </Link>
                )}
              </div>
              <div className={`mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 ${isSold ? 'opacity-60' : ''}`}>
                <p
                  className={`text-3xl sm:text-4xl font-bold ${isSold ? 'text-gray-400 line-through' : 'text-gray-900'}`}
                >
                  {formatListingPrice(displayPrice)}
                </p>
                {listPriceStrike != null && !isSold && (
                  <p className="text-lg text-gray-500 line-through">
                    {formatListingPrice(listPriceStrike)}
                  </p>
                )}
              </div>

              {!isSold && isScheduledForLater && (
                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-start gap-3">
                    <i
                      className={`${isStoreListing ? 'ri-store-2-line' : 'ri-time-line'} text-xl text-gray-500 shrink-0 mt-0.5`}
                      aria-hidden
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900">{scheduleTitle}</p>
                      {scheduleSubtitle && (
                        <p
                          className={`text-sm text-gray-600 mt-0.5${isStoreListing ? ' inline-flex items-center gap-1' : ''}`}
                        >
                          {isStoreListing && (
                            <i className="ri-time-line shrink-0 text-gray-500" aria-hidden />
                          )}
                          {scheduleSubtitle}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!isSold && !isScheduledForLater && (
                <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  <div className="flex items-start gap-3">
                    <div
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-50"
                      aria-hidden
                    >
                      <i className="ri-truck-line text-lg text-green-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-green-700">{deliveryTitle}</p>
                      {orderWithin && (
                        <p className="mt-0.5 inline-flex items-center gap-1 text-sm text-gray-600">
                          <i className="ri-time-line shrink-0 text-gray-500" aria-hidden />
                          {orderWithin}
                        </p>
                      )}
                      {!orderWithin && !deliveryTime && deliveryFee && (
                        <p className="mt-0.5 text-sm text-gray-600">{deliveryFee}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {discountText && !isSold && (
                <div className="mt-4 inline-flex items-center bg-orange-50 text-orange-700 px-3 py-1.5 rounded-lg text-sm font-medium border border-orange-200">
                  <i className="ri-price-tag-3-line mr-2" aria-hidden />
                  <span>{discountText}</span>
                </div>
              )}

              <p className="text-sm text-gray-500 mt-4 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="text-gray-700">
                  {listing.store_id ? 'Local store' : 'Local seller'}
                </span>
                {viewsCount !== null && (
                  <span className="inline-flex items-center gap-1">
                    <span className="text-gray-300" aria-hidden>
                      ·
                    </span>
                    <i className="ri-eye-line text-gray-400 shrink-0" aria-hidden />
                    {viewsCount.toLocaleString()} {viewsCount === 1 ? 'view' : 'views'}
                  </span>
                )}
              </p>

              <div className="pt-4 mt-4 border-t border-gray-200 space-y-2.5">
                {sellerArea && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-map-pin-line text-gray-500 shrink-0" aria-hidden />
                    <span>
                      <span className="text-gray-500">Location:</span> {sellerArea}
                    </span>
                  </div>
                )}
                {conditionLabel && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-shield-check-line text-gray-500 shrink-0" aria-hidden />
                    <span>
                      <span className="text-gray-500">Condition:</span> {conditionLabel}
                    </span>
                  </div>
                )}
                {listing.category && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-grid-line text-gray-500 shrink-0" aria-hidden />
                    <span>
                      <span className="text-gray-500">Category:</span>{' '}
                      <span className="capitalize">{listing.category}</span>
                    </span>
                  </div>
                )}
                {listing.quantity !== undefined && (
                  <div className="flex items-center gap-2 text-sm text-gray-700">
                    <i className="ri-stack-line text-gray-500 shrink-0" aria-hidden />
                    <span>
                      <span className="text-gray-500">Quantity:</span>{' '}
                      {listing.quantity > 0 ? `${listing.quantity} available` : 'Out of stock'}
                    </span>
                  </div>
                )}
              </div>

              {(listing.returns_money_back === true || listing.returns_money_back === 1) && (
                <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <div className="flex items-start gap-2.5">
                    <i className="ri-shield-check-line text-yellow-700 text-lg mt-0.5 shrink-0" aria-hidden />
                    <div>
                      <p className="text-xs font-medium text-yellow-800">Shop with confidence</p>
                      <p className="text-sm font-medium text-yellow-900 mt-0.5">
                        BuyDrop Money Back Guarantee
                      </p>
                      <p className="text-xs text-yellow-800 mt-0.5">
                        Get the item you ordered or your money back.{' '}
                        <Link
                          to="/money-back-guarantee"
                          className="font-medium text-green-700 hover:underline"
                        >
                          Learn more
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {isSold && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <i className="ri-close-circle-line text-2xl text-red-600 shrink-0" aria-hidden />
                    <div>
                      <p className="font-semibold text-red-800">This item has been sold</p>
                      <p className="text-sm text-red-700 mt-0.5">
                        This listing is no longer available for purchase.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {isOwner && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-gray-800 mb-2 font-medium">You own this listing</p>
                  <p className="text-sm text-gray-600 mb-3">
                    Edit, delete, inventory, and orders are managed in the{' '}
                    <strong>BuyDrop Seller</strong> app.
                  </p>
                  <button
                    type="button"
                    onClick={() => navigate('/sell')}
                    className="w-full sm:w-auto px-5 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-semibold"
                  >
                    Open seller app
                  </button>
                </div>
              )}

              {(listing.quantity !== undefined && listing.quantity <= 0) && !isSold && (
                <div className="mt-4 w-full bg-gray-200 text-gray-600 font-semibold py-3 px-4 rounded-lg text-center">
                  Out of stock
                </div>
              )}

              {!isOwner && (listing.quantity === undefined || listing.quantity > 0) && !isSold && (
                <div className="mt-4 space-y-3">
                  {listing.quantity !== undefined && listing.quantity > 1 && (
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <span className="text-sm font-medium text-gray-700">Quantity</span>
                      <div className="flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedQuantity > 1) setSelectedQuantity(selectedQuantity - 1);
                          }}
                          disabled={selectedQuantity <= 1}
                          className="w-9 h-9 shrink-0 flex items-center justify-center bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Decrease quantity"
                        >
                          <i className="ri-subtract-line text-lg" aria-hidden />
                        </button>
                        <span className="w-10 text-center font-semibold text-gray-900 tabular-nums">
                          {selectedQuantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => {
                            if (selectedQuantity < listing.quantity!)
                              setSelectedQuantity(selectedQuantity + 1);
                          }}
                          disabled={selectedQuantity >= listing.quantity!}
                          className="w-9 h-9 shrink-0 flex items-center justify-center bg-white border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                          aria-label="Increase quantity"
                        >
                          <i className="ri-add-line text-lg" aria-hidden />
                        </button>
                      </div>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (processingBuyNow) return;
                      const listingsToCheckout = Array(selectedQuantity)
                        .fill(null)
                        .map(() => ({ ...listing }));
                      if (!user) {
                        try {
                          sessionStorage.setItem(
                            'pendingCheckoutListings',
                            JSON.stringify(listingsToCheckout),
                          );
                        } catch {
                          /* private mode */
                        }
                        navigate('/login', { state: { from: '/checkout' } });
                        return;
                      }
                      setProcessingBuyNow(true);
                      navigate('/checkout', { state: { listings: listingsToCheckout } });
                    }}
                    disabled={processingBuyNow}
                    className="w-full min-h-[48px] px-5 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {processingBuyNow ? (
                      <i className="ri-loader-4-line animate-spin shrink-0 text-xl" aria-hidden />
                    ) : null}
                    <span>
                      Checkout{selectedQuantity > 1 ? ` (${selectedQuantity})` : ''}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!user) {
                        if (id) navigate('/login', loginStateFromListing(id));
                        else navigate('/login');
                        return;
                      }
                      if (isAddingToCart) return;
                      setIsAddingToCart(true);
                      addToCart(listing, selectedQuantity);
                      toast.success('Added to cart');
                      setTimeout(() => setIsAddingToCart(false), 400);
                    }}
                    disabled={isAddingToCart}
                    className="w-full min-h-[48px] px-5 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isAddingToCart ? (
                      <i className="ri-loader-4-line animate-spin shrink-0 text-xl" aria-hidden />
                    ) : (
                      <i className="ri-shopping-cart-line shrink-0 text-xl" aria-hidden />
                    )}
                    <span>
                      Add to cart{selectedQuantity > 1 ? ` (${selectedQuantity})` : ''}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!user) {
                        if (id) navigate('/login', loginStateFromListing(id));
                        else navigate('/login');
                        return;
                      }
                      navigate('/messages', {
                        state: { sellerId: listing.user_id, listingId: listing.id },
                      });
                    }}
                    className="w-full min-h-[48px] px-5 py-3 bg-white border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold rounded-lg inline-flex items-center justify-center gap-2"
                  >
                    <i className="ri-message-line shrink-0 text-xl" aria-hidden />
                    <span>Message Seller</span>
                  </button>
                </div>
              )}
            </div>

            <div className="hidden lg:block">{soldByCard}</div>
          </div>

          <div className="lg:hidden">{soldByCard}</div>

          <div className="lg:col-start-1 lg:row-start-2 space-y-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
              <h3 className="font-semibold text-gray-900 mb-3">Description</h3>
              {!descriptionText ? (
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed text-sm sm:text-base">
                  No description provided.
                </p>
              ) : (
                <>
                  <p className="text-gray-700 whitespace-pre-wrap leading-relaxed text-sm sm:text-base">
                    {showFullDescription || !descriptionPreview.isLong
                      ? descriptionText
                      : `${descriptionPreview.preview}… `}
                    {descriptionPreview.isLong && !showFullDescription && (
                      <button
                        type="button"
                        onClick={() => setShowFullDescription(true)}
                        className="font-medium text-green-700 hover:underline"
                      >
                        See more
                      </button>
                    )}
                  </p>
                  {descriptionPreview.isLong && showFullDescription && (
                    <button
                      type="button"
                      onClick={() => setShowFullDescription(false)}
                      className="mt-2 text-sm font-medium text-green-700 hover:underline"
                    >
                      See less
                    </button>
                  )}
                </>
              )}
            </div>

            {hoursObj && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <h3 className="font-semibold text-gray-900">{hoursSectionTitle}</h3>
                  {hoursBadgeLabel && storeOpenStatus.isOpen === true && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {hoursBadgeLabel}
                    </span>
                  )}
                  {hoursBadgeLabel && storeOpenStatus.isOpen === false && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                      {hoursBadgeLabel}
                    </span>
                  )}
                </div>
                {closedLabel && storeOpenStatus.isOpen === false && (
                  <p className="text-sm text-gray-600 mb-3">{closedLabel}</p>
                )}
                {storeOpenStatus.isOpen === true &&
                  todayStoreHours &&
                  !todayStoreHours.closed &&
                  todayStoreHours.open &&
                  todayStoreHours.close && (
                    <p className="text-sm text-gray-900 mb-3">
                      {isStoreListing ? (
                        <>
                          Open today{' '}
                          <span className="text-gray-600">
                            {formatStoreTime(todayStoreHours.open)} –{' '}
                            {formatStoreTime(todayStoreHours.close)}
                          </span>
                        </>
                      ) : (
                        <span className="text-gray-600">
                          {formatStoreTime(todayStoreHours.open)} –{' '}
                          {formatStoreTime(todayStoreHours.close)}
                        </span>
                      )}
                    </p>
                  )}
                {storeOpenStatus.isOpen === true &&
                  !todayStoreHours &&
                  storeOpenStatus.todayOpen &&
                  storeOpenStatus.todayClose && (
                    <p className="text-sm text-gray-900 mb-3">
                      {isStoreListing ? (
                        <>
                          Open today{' '}
                          <span className="text-gray-600">
                            {formatStoreTime(storeOpenStatus.todayOpen)} –{' '}
                            {formatStoreTime(storeOpenStatus.todayClose)}
                          </span>
                        </>
                      ) : (
                        <span className="text-gray-600">
                          {formatStoreTime(storeOpenStatus.todayOpen)} –{' '}
                          {formatStoreTime(storeOpenStatus.todayClose)}
                        </span>
                      )}
                    </p>
                  )}
                <button
                  type="button"
                  onClick={() => setShowWeeklyHours(!showWeeklyHours)}
                  aria-expanded={showWeeklyHours}
                  className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                >
                  {showWeeklyHours ? 'Hide weekly hours' : 'View weekly hours'}
                  <i
                    className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${showWeeklyHours ? 'rotate-180' : ''}`}
                    aria-hidden
                  />
                </button>
                {showWeeklyHours && (
                  <div className="mt-4 space-y-2 border-t border-gray-100 pt-4">
                    {Object.entries(hoursObj!).map(([day, { open, close, closed }]) => (
                      <div
                        key={day}
                        className="flex items-center justify-between py-1.5 text-sm border-b border-gray-50 last:border-0"
                      >
                        <span className="text-gray-700 font-medium">{day}</span>
                        {closed ? (
                          <span className="text-gray-500">Closed</span>
                        ) : (
                          <span className="text-gray-900">
                            {formatStoreTime(open)} – {formatStoreTime(close)}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {(mapLoading || mapCoords || locationAddress) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 sm:p-6">
                <h3 className="font-semibold text-gray-900 mb-3">Location</h3>
                {locationAddress && (
                  <p className="text-sm text-gray-600 mb-2">{locationAddress}</p>
                )}
                <div
                  className="rounded-lg overflow-hidden border border-gray-200"
                  style={{ height: '400px', minHeight: '400px' }}
                >
                  {mapLoading ? (
                    <div className="w-full h-full flex items-center justify-center bg-gray-50">
                      <div className="text-center">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mb-2" />
                        <p className="text-sm text-gray-600">Loading map location...</p>
                      </div>
                    </div>
                  ) : mapCoords ? (
                    <MapboxMapWrapper
                      center={mapCoords}
                      zoom={15}
                      markers={[
                        {
                          position: mapCoords,
                          content: (
                            <div className="bg-green-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-lg shadow-lg">
                              📍
                            </div>
                          ),
                          popup: (
                            <div className="text-sm font-medium">
                              {locationAddress || `${listing.city_name}, ${listing.state_abbreviation}`}
                            </div>
                          ),
                        },
                      ]}
                      height="400px"
                      scrollZoom={true}
                      className="rounded-lg"
                      style={{ width: '100%', height: '400px', minHeight: '400px' }}
                      onLoad={() => {
                        console.log('✅ Map loaded successfully');
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-50">
                      <p className="text-sm text-gray-500">Map unavailable</p>
                    </div>
                  )}
                </div>
                {!isSold && (
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm font-medium text-gray-900">Local delivery available</p>
                    {(buyerDistanceMiles != null || maxDeliveryMiles !== null) && (
                      <p className="mt-0.5 text-sm text-gray-600">
                        {maxDeliveryMiles !== null && (
                          <>
                            Within {maxDeliveryMiles} {maxDeliveryMiles === 1 ? 'mile' : 'miles'}
                          </>
                        )}
                        {buyerDistanceMiles != null && maxDeliveryMiles !== null && (
                          <span className="text-gray-400" aria-hidden>
                            {' '}
                            ·{' '}
                          </span>
                        )}
                        {buyerDistanceMiles != null && (
                          <>
                            {buyerDistanceMiles < 10
                              ? buyerDistanceMiles.toFixed(1)
                              : Math.round(buyerDistanceMiles)}{' '}
                            {buyerDistanceMiles === 1 ? 'mile' : 'miles'} away
                          </>
                        )}
                      </p>
                    )}
                    {deliveryFeeTooltip && deliveryFeeTooltip.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-sm font-medium text-gray-900">Delivery fees</p>
                        <ul className="mt-0.5 space-y-0.5">
                          {deliveryFeeTooltip.map((line) => (
                            <li key={line} className="text-sm text-gray-600">
                              {line}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Offer Modal */}
        <OfferModal
          listingId={listing.id}
          listingPrice={listing.price}
          isOpen={showOfferModal}
          onClose={() => setShowOfferModal(false)}
          onSuccess={() => {
            loadOffers();
          }}
        />

        {/* Reviews Section - Show "Reviews for this item" only if item reviews exist, otherwise default to "Seller Reviews" */}
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">
              {listingShowsItemReviews(listing)
                ? `Reviews for this item (${listing.item_rating?.total_ratings ?? 0})`
                : `Seller Reviews (${sellerReviews.length || 0})`}
            </h2>
            {((listingShowsItemReviews(listing)) || 
              (!listingShowsItemReviews(listing) && sellerReviews.length > 0)) && (
              <button
                onClick={() => setShowAllItemReviewsModal(true)}
                className="text-green-600 hover:text-green-700 font-medium flex items-center gap-1 transition"
              >
                View All Reviews
                <i className="ri-arrow-right-line"></i>
              </button>
            )}
          </div>
        
          {/* Rating Summary - Show for item reviews OR seller reviews */}
          {(() => {
            const isItemReviews = listingShowsItemReviews(listing);
            const isSellerReviews = !isItemReviews && sellerReviews.length > 0;
            
            if (!isItemReviews && !isSellerReviews) return null;
            
            // Calculate seller review stats if showing seller reviews
            let averageRating = 0;
            let wouldRecommendPercent = 0;
            let goodQualityPercent = 0;
            let valueForPricePercent = 0;
            
            if (isItemReviews) {
              averageRating = listing.item_rating?.average_rating || 0;
              wouldRecommendPercent = listing.item_rating?.would_recommend_percentage || 0;
              goodQualityPercent = listing.item_rating?.good_quality_percentage || 0;
              valueForPricePercent = listing.item_rating?.value_for_price_percentage || 0;
            } else if (isSellerReviews) {
              // Prefer server aggregate for the correct rating system (store vs seller).
              // Review list ratings are rounded to whole stars and must not be re-averaged.
              const serverAvg = listing.store_id
                ? store?.ratings?.average
                : sellerProfile?.ratings?.average;
              if (serverAvg != null && Number.isFinite(Number(serverAvg))) {
                averageRating = Number(serverAvg);
              } else {
                const total = sellerReviews.length;
                if (total > 0) {
                  const sum = sellerReviews.reduce((acc: number, r: any) => acc + (r.rating || 0), 0);
                  averageRating = sum / total;
                }
              }
            }

            const feedbackPositivePct = listing.store_id
              ? ((listing.store_badge?.total_ratings ?? 0) > 0
                  ? listing.store_badge?.positive_percentage
                  : null) ??
                ((store?.ratings?.total ?? 0) > 0 ? store?.ratings?.positive_percentage : null)
              : ((listing.seller_badge?.total_ratings ?? 0) > 0
                  ? listing.seller_badge?.positive_percentage
                  : null) ??
                ((sellerProfile?.ratings?.total ?? 0) > 0
                  ? sellerProfile?.ratings?.positive_percentage
                  : null);
            const feedbackTotal = listing.store_id
              ? pickPositiveTotal(
                  listing.store_badge?.total_ratings,
                  store?.ratings?.total,
                  sellerReviews.length
                )
              : pickPositiveTotal(
                  listing.seller_badge?.total_ratings,
                  sellerProfile?.ratings?.total,
                  sellerReviews.length
                );
            
            return (
              <div className="mb-6 pb-6 border-b border-gray-200">
                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                  {/* Overall Rating - Outside Card */}
                  <div className="flex flex-col items-start flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <i className="ri-star-fill text-3xl sm:text-4xl text-yellow-400"></i>
                      <div className="text-3xl sm:text-4xl font-bold text-gray-900">
                        {averageRating.toFixed(2)}
                      </div>
                      <div className="text-gray-500 text-base sm:text-lg">/5</div>
                    </div>
                    <div className="text-xs sm:text-sm text-gray-600 mt-1 ml-11 sm:ml-14">
                      {isItemReviews ? 'item average' : (listing.store_id ? 'store average' : 'seller average')}
                    </div>
                    {!isItemReviews && feedbackPositivePct != null && feedbackTotal > 0 && (
                      <p className="text-sm text-gray-700 mt-2 ml-0 sm:ml-11 flex items-center gap-1.5">
                        <i className="ri-checkbox-circle-fill text-green-500" aria-hidden />
                        <span>
                          {Number(feedbackPositivePct) % 1 === 0
                            ? Number(feedbackPositivePct).toFixed(0)
                            : Number(feedbackPositivePct).toFixed(1)}
                          % Buyer Approved · {feedbackTotal}{' '}
                          {feedbackTotal === 1 ? 'rating' : 'ratings'}
                        </span>
                      </p>
                    )}
                  </div>
                  
                  {/* Rating Cards - Horizontal Scroll on Mobile */}
                  <div className="w-full sm:w-auto sm:ml-auto overflow-x-auto sm:overflow-visible -mx-4 sm:mx-0 px-4 sm:px-0">
                    <div className="flex sm:block min-w-max sm:min-w-0">
                      {/* Rating Percentages - Inside Card - Float Right (for item reviews) */}
                      {isItemReviews && (
                        <div className="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm min-w-[280px] sm:min-w-[280px]">
                          <div className="space-y-3">
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-700">Would recommend</span>
                              <span className="text-xs font-semibold text-gray-900">
                                {wouldRecommendPercent.toFixed(0)}%
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-700">Good quality / As described</span>
                              <span className="text-xs font-semibold text-gray-900">
                                {goodQualityPercent.toFixed(0)}%
                              </span>
                            </div>
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-gray-700">Value for price</span>
                              <span className="text-xs font-semibold text-gray-900">
                                {valueForPricePercent.toFixed(0)}%
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Horizontal Rating Bars - Inside Card - Float Right (for seller reviews) */}
                      {isSellerReviews && (() => {
                        const detailed = sellerProfile?.ratings?.detailed_ratings;
                        const desc = Number(detailed?.accurate_description) || averageRating;
                        const ship = Number(detailed?.shipping_speed) || averageRating;
                        const comm = Number(detailed?.communication) || averageRating;
                        const bar = (label: string, value: number) => (
                          <div className="flex items-center gap-3" key={label}>
                            <span className="text-xs font-medium text-gray-700 w-28">{label}</span>
                            <div className="flex items-center gap-2 flex-1">
                              <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-orange-500 rounded-full transition-all"
                                  style={{ width: `${Math.min(100, Math.max(0, (value / 5.0) * 100))}%` }}
                                ></div>
                              </div>
                              <span className="text-xs font-semibold text-gray-900 w-8 text-right">
                                {value.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        );
                        return (
                          <div className="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm min-w-[280px] sm:min-w-[280px]">
                            <div className="space-y-2.5">
                              {bar('Accurate description', desc)}
                              {bar('Shipping speed', ship)}
                              {bar('Communication', comm)}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Individual Reviews - Show item reviews if available, otherwise show seller reviews */}
          {(() => {
            const isItemReviews = listingShowsItemReviews(listing);
            const reviewsToShow = isItemReviews ? itemReviews : sellerReviews;
            const loading = isItemReviews ? loadingItemReviews : loadingReviews;
            const reviewsToDisplay = showMoreReviews ? reviewsToShow : reviewsToShow.slice(0, 3);
            const hasMoreReviews = reviewsToShow.length > 3;
            
            if (isItemReviews) {
              if (loading) {
                return <p className="text-gray-500">Loading reviews...</p>;
              }
              if (itemReviews.length === 0) {
                return <p className="text-gray-500">No individual reviews available yet.</p>;
              }
            } else {
              if (sellerReviews.length === 0) {
                return (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No reviews yet. Be the first to review this item!</p>
                  </div>
                );
              }
            }
            
            return (
              <>
                <div className="space-y-4">
                  {reviewsToDisplay.map((review: any) => (
                    <div
                      key={review.id}
                      className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 shadow-sm"
                    >
                      <div className="flex items-start gap-2 sm:gap-3">
                        <div className="flex-shrink-0">{renderStars(review.rating)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm sm:text-base text-gray-900 mb-1">
                            {review.buyer_name || 'Anonymous'}
                          </div>
                          <div className="text-xs text-gray-500 mb-2">
                            {new Date(review.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                          </div>
                          {review.comment && (
                            <p className="text-gray-700 text-xs sm:text-sm break-words">
                              {review.comment}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {hasMoreReviews && !showMoreReviews && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => setShowMoreReviews(true)}
                      className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-medium rounded-lg transition-colors text-sm sm:text-base"
                    >
                      Show More Reviews ({reviewsToShow.length - 3} more)
                    </button>
                  </div>
                )}
                {hasMoreReviews && showMoreReviews && (
                  <div className="mt-4 text-center">
                    <button
                      onClick={() => setShowMoreReviews(false)}
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg transition-colors text-sm sm:text-base"
                    >
                      Show Less
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>

        {/* Feedback Ratings Modal */}
        {showFeedbackModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4" onClick={() => setShowFeedbackModal(false)}>
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 pr-2">Feedback ratings</h2>
                <button
                  onClick={() => setShowFeedbackModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition flex-shrink-0"
                >
                  <i className="ri-close-line text-xl sm:text-2xl"></i>
                </button>
              </div>
              <div className="p-4 sm:p-6">
                {loadingFeedback ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">Loading feedback...</p>
                  </div>
                ) : feedbackData ? (
                  <>
                    {/* Last 12 Months Section */}
                    <div className="mb-4 sm:mb-6">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Last 12 months</h3>
                      <div className="space-y-2 sm:space-y-3">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 p-3 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-2">
                            <i className="ri-thumb-up-line text-green-600 text-lg sm:text-xl"></i>
                            <span className="font-medium text-gray-900 text-sm sm:text-base">Approved</span>
                          </div>
                          <span className="text-gray-700 font-semibold text-xs sm:text-sm">
                            {feedbackData.last_12_months.positive.toLocaleString()} - approved feedback in last 12 months
                          </span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 p-3 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex items-center gap-2">
                            <i className="ri-question-line text-gray-600 text-lg sm:text-xl"></i>
                            <span className="font-medium text-gray-900 text-sm sm:text-base">Neutral</span>
                          </div>
                          <span className="text-gray-700 font-semibold text-xs sm:text-sm">
                            {feedbackData.last_12_months.neutral.toLocaleString()} - neutral feedback in last 12 months
                          </span>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 p-3 bg-red-50 rounded-lg border border-red-200">
                          <div className="flex items-center gap-2">
                            <i className="ri-thumb-down-line text-red-600 text-lg sm:text-xl"></i>
                            <span className="font-medium text-gray-900 text-sm sm:text-base">Not approved</span>
                          </div>
                          <span className="text-gray-700 font-semibold text-xs sm:text-sm">
                            {feedbackData.last_12_months.negative.toLocaleString()} - not approved feedback in last 12 months
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Detailed Seller Ratings Section */}
                    {feedbackData.detailed_ratings && feedbackData.detailed_ratings.total_ratings > 0 && (
                      <div className="border-t border-gray-200 pt-4 sm:pt-6">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Detailed seller ratings</h3>
                        <div className="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm min-h-[140px] sm:min-h-[180px] flex items-center">
                          <div className="space-y-2.5 w-full">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <span className="text-xs font-medium text-gray-700 w-24 sm:w-32 flex-shrink-0">Communication</span>
                              <div className="flex items-center gap-2 flex-1">
                                <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-orange-500 rounded-full transition-all"
                                    style={{ 
                                      width: `${(() => {
                                        const rating = parseFloat(feedbackData.detailed_ratings.communication) || 0;
                                        return (rating / 5.0) * 100;
                                      })()}%` 
                                    }}
                                  ></div>
                                </div>
                                <span className="text-xs font-semibold text-gray-900 w-8 text-right">
                                  {feedbackData.detailed_ratings.communication || '0.0'}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 sm:gap-3">
                              <span className="text-xs font-medium text-gray-700 w-24 sm:w-32 flex-shrink-0">Shipping speed</span>
                              <div className="flex items-center gap-2 flex-1">
                                <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-orange-500 rounded-full transition-all"
                                    style={{ 
                                      width: `${(() => {
                                        const rating = parseFloat(feedbackData.detailed_ratings.shipping_speed) || 0;
                                        return (rating / 5.0) * 100;
                                      })()}%` 
                                    }}
                                  ></div>
                                </div>
                                <span className="text-xs font-semibold text-gray-900 w-8 text-right flex-shrink-0">
                                  {feedbackData.detailed_ratings.shipping_speed || '0.0'}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 sm:gap-3">
                              <span className="text-xs font-medium text-gray-700 w-24 sm:w-32 flex-shrink-0">Accurate description</span>
                              <div className="flex items-center gap-2 flex-1">
                                <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div 
                                    className="h-full bg-orange-500 rounded-full transition-all"
                                    style={{ 
                                      width: `${(() => {
                                        const rating = parseFloat(feedbackData.detailed_ratings.accurate_description) || 0;
                                        return (rating / 5.0) * 100;
                                      })()}%` 
                                    }}
                                  ></div>
                                </div>
                                <span className="text-xs font-semibold text-gray-900 w-8 text-right">
                                  {feedbackData.detailed_ratings.accurate_description || '0.0'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No feedback data available.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* All Item Reviews Modal with Rating Details */}
        {showAllItemReviewsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4" onClick={() => setShowAllItemReviewsModal(false)}>
            <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
                <h2 className="text-lg sm:text-2xl font-bold text-gray-900 pr-2">
                  {!listingShowsItemReviews(listing) && sellerReviews.length > 0
                    ? `Seller Reviews (${sellerReviews.length})`
                    : `Reviews for this item (${listing.item_rating?.total_ratings || 0})`}
                </h2>
                <button
                  onClick={() => setShowAllItemReviewsModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition flex-shrink-0"
                >
                  <i className="ri-close-line text-xl sm:text-2xl"></i>
                </button>
              </div>
              <div className="p-4 sm:p-6">
                {/* Rating Summary in Dialog - Show for item reviews OR seller reviews */}
                {(() => {
                  const isItemReviews = listingShowsItemReviews(listing);
                  const isSellerReviews = !isItemReviews && sellerReviews.length > 0;
                  
                  if (!isItemReviews && !isSellerReviews) return null;
                  
                  // Calculate seller review stats if showing seller reviews
                  let averageRating = 0;
                  let wouldRecommendPercent = 0;
                  let goodQualityPercent = 0;
                  let valueForPricePercent = 0;
                  
                  if (isItemReviews) {
                    averageRating = listing.item_rating?.average_rating || 0;
                    wouldRecommendPercent = listing.item_rating?.would_recommend_percentage || 0;
                    goodQualityPercent = listing.item_rating?.good_quality_percentage || 0;
                    valueForPricePercent = listing.item_rating?.value_for_price_percentage || 0;
                  } else if (isSellerReviews) {
                    const serverAvg = listing.store_id
                      ? store?.ratings?.average
                      : sellerProfile?.ratings?.average;
                    if (serverAvg != null && Number.isFinite(Number(serverAvg))) {
                      averageRating = Number(serverAvg);
                    } else {
                      const total = sellerReviews.length;
                      if (total > 0) {
                        const sum = sellerReviews.reduce((acc: number, r: any) => acc + (r.rating || 0), 0);
                        averageRating = sum / total;
                      }
                    }
                  }

                  const feedbackPositivePct = listing.store_id
                    ? ((listing.store_badge?.total_ratings ?? 0) > 0
                        ? listing.store_badge?.positive_percentage
                        : null) ??
                      ((store?.ratings?.total ?? 0) > 0 ? store?.ratings?.positive_percentage : null)
                    : ((listing.seller_badge?.total_ratings ?? 0) > 0
                        ? listing.seller_badge?.positive_percentage
                        : null) ??
                      ((sellerProfile?.ratings?.total ?? 0) > 0
                        ? sellerProfile?.ratings?.positive_percentage
                        : null);
                  const feedbackTotal = listing.store_id
                    ? pickPositiveTotal(
                        listing.store_badge?.total_ratings,
                        store?.ratings?.total,
                        sellerReviews.length
                      )
                    : pickPositiveTotal(
                        listing.seller_badge?.total_ratings,
                        sellerProfile?.ratings?.total,
                        sellerReviews.length
                      );
                  
                  return (
                    <div className="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                      <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
                        {/* Overall Rating - Outside Card */}
                        <div className="flex flex-col items-start w-full sm:w-auto">
                          <div className="flex items-center gap-2">
                            <i className="ri-star-fill text-3xl sm:text-4xl text-yellow-400"></i>
                            <div className="text-3xl sm:text-4xl font-bold text-gray-900">
                              {averageRating.toFixed(2)}
                            </div>
                            <div className="text-gray-500 text-base sm:text-lg">/5</div>
                          </div>
                          <div className="text-xs sm:text-sm text-gray-600 mt-1 ml-11 sm:ml-14">
                            {isItemReviews ? 'item average' : (listing.store_id ? 'store average' : 'seller average')}
                          </div>
                          {!isItemReviews && feedbackPositivePct != null && feedbackTotal > 0 && (
                            <p className="text-sm text-gray-700 mt-2 ml-0 sm:ml-11 flex items-center gap-1.5">
                              <i className="ri-checkbox-circle-fill text-green-500" aria-hidden />
                              <span>
                                {Number(feedbackPositivePct) % 1 === 0
                                  ? Number(feedbackPositivePct).toFixed(0)
                                  : Number(feedbackPositivePct).toFixed(1)}
                                % Buyer Approved · {feedbackTotal}{' '}
                                {feedbackTotal === 1 ? 'rating' : 'ratings'}
                              </span>
                            </p>
                          )}
                        </div>
                        
                        {/* Rating Percentages - Inside Card - Float Right (for item reviews) */}
                        {isItemReviews && (
                          <div className="w-full sm:ml-auto sm:w-auto bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm">
                            <div className="space-y-3 w-full sm:min-w-[280px]">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-700">Would recommend</span>
                                <span className="text-xs font-semibold text-gray-900">
                                  {wouldRecommendPercent.toFixed(0)}%
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-700">Good quality / As described</span>
                                <span className="text-xs font-semibold text-gray-900">
                                  {goodQualityPercent.toFixed(0)}%
                                </span>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-medium text-gray-700">Value for price</span>
                                <span className="text-xs font-semibold text-gray-900">
                                  {valueForPricePercent.toFixed(0)}%
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Horizontal Rating Bars - Inside Card - Float Right (for seller reviews) */}
                        {isSellerReviews && (() => {
                          const detailed = sellerProfile?.ratings?.detailed_ratings;
                          const desc = Number(detailed?.accurate_description) || averageRating;
                          const ship = Number(detailed?.shipping_speed) || averageRating;
                          const comm = Number(detailed?.communication) || averageRating;
                          const bar = (label: string, value: number) => (
                            <div className="flex items-center gap-2 sm:gap-3" key={label}>
                              <span className="text-xs font-medium text-gray-700 w-24 sm:w-28 flex-shrink-0">{label}</span>
                              <div className="flex items-center gap-2 flex-1">
                                <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-orange-500 rounded-full transition-all"
                                    style={{ width: `${Math.min(100, Math.max(0, (value / 5.0) * 100))}%` }}
                                  ></div>
                                </div>
                                <span className="text-xs font-semibold text-gray-900 w-8 text-right flex-shrink-0">
                                  {value.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          );
                          return (
                            <div className="w-full sm:ml-auto sm:w-auto bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200 shadow-sm">
                              <div className="space-y-2.5 w-full sm:min-w-[280px]">
                                {bar('Accurate description', desc)}
                                {bar('Shipping speed', ship)}
                                {bar('Communication', comm)}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  );
                })()}
                
                {/* All Reviews - Show item reviews or seller reviews */}
                <div className="space-y-3 sm:space-y-4">
                  {!listingShowsItemReviews(listing) && sellerReviews.length > 0 ? (
                    sellerReviews.map((review: any) => (
                      <div
                        key={review.id}
                        className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 shadow-sm"
                      >
                        <div className="flex items-start gap-2 sm:gap-3">
                          {renderStars(review.rating)}
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900 mb-1">
                              {review.buyer_name || 'Anonymous'}
                            </div>
                            <div className="text-xs text-gray-500 mb-2">
                              {new Date(review.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                            </div>
                            {review.comment && (
                              <p className="text-gray-700 text-sm">
                                {review.comment}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : itemReviews.length > 0 ? (
                    itemReviews.map((review: any) => (
                      <div
                        key={review.id}
                        className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 shadow-sm"
                      >
                        <div className="flex items-start gap-2 sm:gap-3">
                          {renderStars(review.rating)}
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900 mb-1">
                              {review.buyer_name || 'Anonymous'}
                            </div>
                            <div className="text-xs text-gray-500 mb-2">
                              {new Date(review.created_at).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })}
                            </div>
                            {review.comment && (
                              <p className="text-gray-700 text-sm">
                                {review.comment}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p className="text-gray-500 text-center py-8">No reviews available yet.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Show Offers Section (for seller) */}
        {isOwner && offers.length > 0 && (
          <div className="mt-6 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Offers
              {offers.filter(o => o.status === 'pending').length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
                  {offers.filter(o => o.status === 'pending').length}
                </span>
              )}
            </h2>
            {loadingOffers ? (
              <p className="text-gray-500">Loading offers...</p>
            ) : (
              <div className="space-y-4">
                {offers.map((offer) => (
                <div
                  key={offer.id}
                  className={`border rounded-lg p-4 ${
                    offer.status === 'accepted' ? 'border-green-500 bg-green-50' :
                    offer.status === 'rejected' ? 'border-red-500 bg-red-50' :
                    offer.status === 'cancelled' ? 'border-gray-300 bg-gray-50' :
                    'border-gray-200 bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="font-semibold text-lg text-gray-900">
                          ${offer.offer_amount.toLocaleString()}
                        </span>
                        {offer.status === 'pending' && (
                          <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-medium rounded">
                            Pending
                          </span>
                        )}
                        {offer.status === 'accepted' && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                            Accepted
                          </span>
                        )}
                        {offer.status === 'rejected' && (
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-medium rounded">
                            Rejected
                          </span>
                        )}
                        {offer.status === 'cancelled' && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs font-medium rounded">
                            Cancelled
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-1">
                        From: <span className="font-medium">{offer.buyer_name}</span>
                      </p>
                      {offer.message && (
                        <p className="text-sm text-gray-700 mt-2 italic">"{offer.message}"</p>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(offer.created_at).toLocaleString()}
                      </p>
                    </div>
                    {offer.status === 'pending' && (
                      <div className="flex space-x-2 ml-4">
                        <button
                          onClick={async () => {
                            try {
                              await offersAPI.accept(offer.id);
                              toast.success('Offer accepted! Manage the order in the BuyDrop Seller app.');
                              navigate('/sell');
                            } catch (err: any) {
                              const errorMsg = err.response?.data?.error || 'Failed to accept offer';
                              if (err.response?.data?.requiresAddress) {
                                // Old offer without delivery address - prompt for it
                                const deliveryAddress = prompt('This offer was created before delivery addresses were required. Please enter the delivery address:');
                                if (!deliveryAddress) return;
                                try {
                                  await offersAPI.accept(offer.id, deliveryAddress);
                                  toast.success('Offer accepted! Manage the order in the BuyDrop Seller app.');
                                  navigate('/sell');
                                } catch (retryErr: any) {
                                  toast.error(retryErr.response?.data?.error || 'Failed to accept offer');
                                }
                              } else {
                                toast.error(errorMsg);
                              }
                            }
                          }}
                          className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition"
                        >
                          Accept
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm('Are you sure you want to reject this offer?')) return;
                            try {
                              await offersAPI.reject(offer.id);
                              loadOffers();
                            } catch (err: any) {
                              toast.error(err.response?.data?.error || 'Failed to reject offer');
                            }
                          }}
                          className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition"
                        >
                          Reject
                        </button>
                        <button
                          onClick={async () => {
                            if (!window.confirm('Are you sure you want to delete this offer?')) return;
                            try {
                              await offersAPI.reject(offer.id);
                              loadOffers();
                            } catch (err: any) {
                              toast.error(err.response?.data?.error || 'Failed to delete offer');
                            }
                          }}
                          className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white text-sm font-medium rounded-lg transition"
                          title="Delete Offer"
                        >
                          🗑️
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              </div>
            )}
          </div>
        )}

        {/* Recommended based on views, cart adds, and sales */}
        {recommended.length > 0 && (
          <div className="mt-8 px-4 sm:px-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Recommended for you</h2>
            <p className="text-sm text-gray-500 mb-4">
              {recommendationArea ? `Near ${recommendationArea}. ` : ''}
              Based on what others viewed, added to cart, and bought
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {recommended.slice(0, 20).map((item) => (
                <Link key={item.id} to={`/listings/${item.id}`} className="block group">
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:border-green-300 hover:shadow-md transition-all">
                    <div className="relative w-full aspect-[4/3] bg-gray-50 overflow-hidden">
                      <ListingImage
                        src={item.image_url || '/placeholder.png'}
                        size="card"
                        alt={item.title}
                        className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-300 ease-out group-hover:scale-110"
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                    <div className="p-2">
                      <p className="font-medium text-gray-900 text-sm line-clamp-2 group-hover:text-green-600">{item.title}</p>
                      <p className="text-green-600 font-bold mt-1">${Number(item.price || 0).toLocaleString()}</p>
                      {(item.city_name || item.state_abbreviation) && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">
                          {[item.city_name, item.state_abbreviation].filter(Boolean).join(', ')}
                        </p>
                      )}
                      {(item.views_count > 0 || item.cart_adds_count > 0) && (
                        <p className="text-xs text-gray-500 mt-0.5">
                          {[item.views_count > 0 && `${item.views_count} views`, item.cart_adds_count > 0 && `${item.cart_adds_count} in cart`].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ListingDetail;

